import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  dialogClasses,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
} from '@montugroup/design-system';
import { useDebouncedCallback } from '@montugroup/frontend-common';
import { useMediaQuery, useTheme } from '@mui/material';
import type { CellContext, Row } from '@tanstack/react-table';
import { useMemo, useState } from 'react';

import TableSearchInput from '@/components/common/TableSearchInput';
import type { AddableProduct } from '@/hooks/stocktake/useStocktake';

import { compareByName } from './stocktakeSort';
import { numericColumnMeta } from './stocktakeTableStyles';

type AddStocktakeProductsModalProps = Readonly<{
  open: boolean;
  products: readonly AddableProduct[];
  onClose: () => void;
  onAdd: (productIds: readonly number[]) => void;
}>;

const INITIAL_SORT = [{ id: 'name', desc: false }];

export const SEARCH_DEBOUNCE_MS = 250;

// Centred, the dialog moves the search box as the number of results changes its height
const TOP_ALIGNED_DIALOG_SX = { [`& .${dialogClasses.container}`]: { alignItems: 'flex-start' } } as const;

// Shares of the table's width, so columns keep their size as paging and searching change the rows
const COLUMN_WIDTHS = {
  select: '5%',
  name: '50%',
  sku: '30%',
  expectedStock: '15%',
} as const;

const matchesSearch = (product: AddableProduct, search: string): boolean => {
  const lowercasedSearch = search.toLowerCase();
  return product.name.toLowerCase().includes(lowercasedSearch) || product.sku.toLowerCase().includes(lowercasedSearch);
};

export function AddStocktakeProductsModal({ open, products, onClose, onAdd }: AddStocktakeProductsModalProps) {
  const [search, setSearch] = useState('');
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<number>>(new Set<number>());
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const handleSearch = useDebouncedCallback(setSearch, SEARCH_DEBOUNCE_MS);

  const filteredProducts = useMemo(
    () => products.filter((product) => matchesSearch(product, search)),
    [products, search],
  );

  const columns = useMemo(() => {
    const toggle = (productId: number) => {
      setSelectedIds((current) => {
        const next = new Set(current);
        if (!next.delete(productId)) {
          next.add(productId);
        }
        return next;
      });
    };

    return [
      {
        id: 'select',
        enableSorting: false,
        header: '',
        meta: { sx: { width: COLUMN_WIDTHS.select } },
        cell: ({ row }: CellContext<AddableProduct, unknown>) => (
          <Checkbox
            checked={selectedIds.has(row.original.productId)}
            onChange={() => toggle(row.original.productId)}
            slotProps={{ input: { 'aria-label': `Select ${row.original.name}` } }}
          />
        ),
      },
      {
        accessorKey: 'name',
        header: 'Product Name',
        enableSorting: true,
        meta: { sx: { width: COLUMN_WIDTHS.name } },
        // The Table's default text sort compares character codes, which puts accented names after 'z'
        sortingFn: (a: Row<AddableProduct>, b: Row<AddableProduct>) => compareByName(a.original, b.original),
      },
      {
        accessorKey: 'sku',
        header: 'SKU',
        enableSorting: true,
        meta: {
          sx: { width: COLUMN_WIDTHS.sku },
          bodySx: { color: 'text.secondary', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
        },
      },
      {
        accessorKey: 'expectedStock',
        header: 'Expected Stock',
        enableSorting: false,
        meta: { ...numericColumnMeta, sx: [numericColumnMeta.sx, { width: COLUMN_WIDTHS.expectedStock }] },
      },
    ];
  }, [selectedIds]);

  const selectedCount = selectedIds.size;

  const onExited = () => {
    // The modal stays mounted while closed, so a search typed just before closing would otherwise land after this reset
    handleSearch.cancel();
    setSearch('');
    setSelectedIds(new Set<number>());
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      maxWidth="lg"
      sx={TOP_ALIGNED_DIALOG_SX}
      slotProps={{ transition: { onExited } }}
    >
      <DialogTitle>Add products</DialogTitle>
      <DialogContent>
        <Stack gap={6} paddingTop={2}>
          <TableSearchInput value={search} onChange={handleSearch} />
          <Table
            data={filteredProducts}
            columns={columns}
            initialSort={INITIAL_SORT}
            showPagination
            hasRowBackgroundColor={false}
            compact
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={selectedCount === 0} onClick={() => onAdd([...selectedIds])}>
          Add {selectedCount} {selectedCount === 1 ? 'product' : 'products'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
